import { usePageTitle, useMetaDescription, useSocialMeta } from '@/hooks/usePageTitle';
import { useLocale } from '@/hooks/useLocale';
import { PublicShell } from '@/components/visual/PublicShell';
import { LocaleLink } from '@/components/LocaleLink';
import { AlertTriangle } from 'lucide-react';
import { useId } from 'react';

const CHIVO = { fontFamily: 'IBM Plex Sans, sans-serif' };
const PATHS = { privacy: '/privacy', terms: '/terms', disclaimer: '/disclaimer' };
const OTHERS = {
  privacy: ['terms', 'disclaimer'],
  terms: ['privacy', 'disclaimer'],
  disclaimer: ['privacy', 'terms'],
};

// Lightweight inline renderer: supports **bold** and [label](href) markdown.
const INLINE_RE = /(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)/g;
function renderInline(text, testPrefix) {
  const nodes = [];
  let last = 0;
  let key = 0;
  let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      nodes.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else {
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      nodes.push(
        <a key={key} data-testid={`${testPrefix}-link-${key++}`} href={lm[2]} className="text-[#527F19] hover:underline">{lm[1]}</a>
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function Bullets({ items }) {
  const id = useId().replaceAll(':', '');
  return (
    <ul x-excluded="true" className="list-disc pl-6 space-y-1 mt-2">
      {items.map((it, i) => {
        const content = typeof it === 'string'
          ? renderInline(it, `legal-bullet-${id}-${i}`)
          : <><strong>{it.term}</strong> {renderInline(it.text, `legal-bullet-${id}-${i}`)}</>;
        return <li key={i}>{content}</li>;
      })}
    </ul>
  );
}

function Paragraphs({ items, amber }) {
  const id = useId().replaceAll(':', '');
  const base = amber ? 'text-[#6E4708]' : '';
  return items.map((p, i) => {
    const cls = `${base}${i ? ' mt-3' : ''}`.trim();
    return <p key={i} className={cls || undefined}>{renderInline(p, `legal-paragraph-${id}-${i}`)}</p>;
  });
}

function Section({ index, section }) {
  const inner = (
    <>
      {section.paragraphs && <Paragraphs items={section.paragraphs} amber={section.box} />}
      {section.bullets && <Bullets items={section.bullets} />}
    </>
  );
  return (
    <section data-testid={`legal-section-${index}`}>
      <h2 className="text-lg font-bold text-[#171A1D] mt-8 mb-3" style={CHIVO}>
        {index}. {section.heading}
      </h2>
      {section.box ? (
        <div className="p-4 rounded-lg bg-[#B7791F]/[0.06] border border-[#B7791F]/25">
          {section.boxLabel && <p className="font-semibold text-[#8A5A0B] mb-2">{section.boxLabel}</p>}
          {inner}
        </div>
      ) : inner}
    </section>
  );
}

export function LegalPageLayout({ docType, bundle }) {
  const { lang } = useLocale();
  const doc = bundle[docType];
  usePageTitle(doc.metaTitle);
  useMetaDescription(doc.metaDescription);
  useSocialMeta({
    title: doc.metaTitle,
    description: doc.metaDescription,
    url: `https://ismscalculator.com/${lang}${PATHS[docType]}`,
  });

  return (
    <PublicShell>
      <main id="main-content" tabIndex={-1} className="site-container reading-width public-main legal-document">
        <h1 className="text-3xl font-semibold tracking-tight mb-2" style={CHIVO} data-testid={`${docType}-heading`}>
          {doc.title}
        </h1>
        <p className="text-sm text-[#66707A] mb-8" data-testid="legal-updated">{doc.lastUpdated}</p>

        <div className="prose prose-slate max-w-none space-y-6 text-[#3A4249] text-sm leading-relaxed">
          {doc.topCallout && (
            <div className="p-6 rounded-lg bg-[#B7791F]/[0.06] border border-[#B7791F]/25">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-[#B7791F] shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-bold text-[#6E4708] mb-2" style={CHIVO}>{doc.topCallout.title}</h2>
                  <Paragraphs items={doc.topCallout.paragraphs} amber />
                </div>
              </div>
            </div>
          )}
          {doc.sections.map((section, i) => (
            <Section key={section.heading} index={i + 1} section={section} />
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-[#DDE1E5] text-sm text-[#66707A]">
          <p>
            <strong>ISMS Calculator</strong> &middot;{' '}
            <a href="mailto:support@ismscalculator.com" className="text-[#527F19] hover:underline" data-testid="legal-support-link">support@ismscalculator.com</a>
          </p>
          <div className="mt-4 flex gap-4 flex-wrap">
            {OTHERS[docType].map((d) => (
              <LocaleLink key={d} to={PATHS[d]} className="text-[#527F19] hover:underline" data-testid={`legal-related-${d}`}>{bundle[d].title}</LocaleLink>
            ))}
            <LocaleLink to="/" className="text-[#527F19] hover:underline" data-testid="legal-home-link">{bundle.nav.home}</LocaleLink>
          </div>
        </div>
      </main>
    </PublicShell>
  );
}
